import React, { useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  EmailField,
  DateField,
  BooleanField,
  EditButton,
  DeleteButton,
  Show,
  SimpleShowLayout,
  Edit,
  Create,
  SimpleForm,
  TextInput,
  PasswordInput,
  BooleanInput,
  SelectInput,
  DateTimeInput,
  DateInput,
  NumberInput,
  ArrayField,
  ReferenceField,
  ReferenceManyField,
  SingleFieldList,
  ChipField,
  NumberField,
  Filter,
  TopToolbar,
  ExportButton,
  CreateButton,
  BulkDeleteButton,
  BulkUpdateButton,
  FunctionField,
  useRecordContext,
  required,
  email,
  minLength,
  useNotify,
  useRefresh,
  Button,
  SimpleList,
  useDataProvider,
  useUnselectAll,
  useListContext,
} from 'react-admin';
import {
  Box,
  Chip,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Paper,
  Alert,
  Divider,
  List as MuiList,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tab,
  Tabs,
  useMediaQuery,
  Theme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  TextField as MuiTextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  ShoppingCart as OrderIcon,
  RateReview as ReviewIcon,
  CardGiftcard as CouponIcon,
  Stars as PointIcon,
  Block as BlockIcon,
  VpnKey as PasswordIcon,
  Verified as VerifiedIcon,
  Add as AddIcon,
  LocalAtm as LocalAtmIcon,
} from '@mui/icons-material';
import { getSupabaseAuth, supabaseAdmin } from '../lib/supabase';

// UTC를 KST로 변환하는 헬퍼 함수 (한국 표준시 +9시간)
// Supabase에서 반환되는 시간에 시간대 정보(Z)가 없으므로 추가해서 UTC로 인식하게 함
const formatKSTDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  // 시간대 정보가 없는 경우 Z를 추가해서 UTC로 인식하게 함
  const utcDateString = dateString.endsWith('Z') || dateString.includes('+') ? dateString : dateString + 'Z';
  const date = new Date(utcDateString);

  // UTC 시간에 9시간을 더해서 KST로 변환
  const kstOffset = 9 * 60 * 60 * 1000;
  const kstTime = new Date(date.getTime() + kstOffset);

  const year = kstTime.getUTCFullYear();
  const month = kstTime.getUTCMonth() + 1;
  const day = kstTime.getUTCDate();
  const hours = kstTime.getUTCHours();
  const minutes = kstTime.getUTCMinutes().toString().padStart(2, '0');
  const seconds = kstTime.getUTCSeconds().toString().padStart(2, '0');

  const ampm = hours < 12 ? '오전' : '오후';
  const hour12 = hours % 12 || 12;

  return `${year}. ${month}. ${day}. ${ampm} ${hour12}:${minutes}:${seconds}`;
};

// User 테이블에서 현재 적립금 조회
const calculateAvailablePoints = async (userId: string): Promise<number> => {
  try {
    const { data: user, error } = await supabaseAdmin
      .from('User')
      .select('points')
      .eq('id', userId)
      .single();

    if (error) throw error;

    return user?.points || 0;
  } catch (error) {
    console.error('적립금 조회 실패:', error);
    return 0;
  }
};

// 적립금 지급 다이얼로그
interface PointGiveDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  currentPoints: number;
  onSuccess: () => void;
}

// 1년 후 날짜를 YYYY-MM-DD 형식으로 반환
const getDefaultExpiryDate = (): string => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return date.toISOString().split('T')[0];
};

const PointGiveDialog = ({ open, onClose, userId, userName, currentPoints, onSuccess }: PointGiveDialogProps) => {
  const [amount, setAmount] = useState<number>(0);
  const [reason, setReason] = useState('');
  const [expiryDate, setExpiryDate] = useState(getDefaultExpiryDate());
  const [loading, setLoading] = useState(false);
  const notify = useNotify();

  const handleSubmit = async () => {
    if (amount === 0) {
      notify('지급/차감할 적립금을 입력해주세요', { type: 'warning' });
      return;
    }
    if (!reason.trim()) {
      notify('지급 사유를 입력해주세요', { type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const newBalance = currentPoints + amount;

      // 1. User.points 업데이트
      const { error: updateError } = await supabaseAdmin
        .from('User')
        .update({ points: newBalance })
        .eq('id', userId);

      if (updateError) throw updateError;

      // 2. PointHistory 생성
      const { error: historyError } = await supabaseAdmin
        .from('PointHistory')
        .insert({
          id: crypto.randomUUID(),
          userId: userId,
          type: amount > 0 ? 'EARNED' : 'USED',
          amount: amount,
          balance: newBalance,
          description: reason,
          expiresAt: expiryDate ? new Date(expiryDate).toISOString() : null,
          createdAt: new Date().toISOString(),
        });

      if (historyError) throw historyError;

      notify(`${userName}님에게 ${amount > 0 ? amount.toLocaleString() + 'P 지급' : Math.abs(amount).toLocaleString() + 'P 차감'} 완료`, { type: 'success' });
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('적립금 지급 실패:', error);
      notify(`적립금 처리 실패: ${error.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAmount(0);
    setReason('');
    setExpiryDate(getDefaultExpiryDate());
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <LocalAtmIcon color="primary" />
          <span>적립금 지급/차감</span>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>{userName}</strong>님의 현재 적립금: <strong>{currentPoints.toLocaleString()}P</strong>
          </Alert>

          <MuiTextField
            label="적립금 금액"
            type="number"
            value={amount}
            onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
            fullWidth
            sx={{ mb: 2 }}
            helperText="양수: 지급 / 음수: 차감"
            InputProps={{
              endAdornment: <InputAdornment position="end">P</InputAdornment>,
            }}
          />

          <MuiTextField
            label="지급/차감 사유"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            rows={2}
            sx={{ mb: 2 }}
            placeholder="예: 이벤트 참여 보상, 불량상품 보상 등"
          />

          <MuiTextField
            label="만료일 (선택사항)"
            type="date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            helperText="기본값: 1년 후 (수정 가능)"
          />

          {amount !== 0 && (
            <Alert severity={amount > 0 ? 'success' : 'warning'} sx={{ mt: 2 }}>
              예상 잔액: <strong>{(currentPoints + amount).toLocaleString()}P</strong>
              {amount > 0 ? ` (+${amount.toLocaleString()}P)` : ` (${amount.toLocaleString()}P)`}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={handleClose} disabled={loading}>
          취소
        </MuiButton>
        <MuiButton
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || amount === 0 || !reason.trim()}
        >
          {loading ? '처리 중...' : amount >= 0 ? '지급하기' : '차감하기'}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
};

// 쿠폰 발급 다이얼로그
interface CouponGiveDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  onSuccess: () => void;
}

const CouponGiveDialog = ({ open, onClose, userId, userName, onSuccess }: CouponGiveDialogProps) => {
  const [coupons, setCoupons] = useState<any[]>([]);
  const [selectedCouponId, setSelectedCouponId] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingCoupons, setFetchingCoupons] = useState(false);
  const notify = useNotify();

  // 쿠폰 목록 조회
  React.useEffect(() => {
    if (open) {
      setFetchingCoupons(true);
      supabaseAdmin
        .from('Coupon')
        .select('*')
        .eq('isActive', true)
        .order('createdAt', { ascending: false })
        .then(({ data, error }) => {
          if (error) {
            console.error('쿠폰 목록 조회 실패:', error);
            notify('쿠폰 목록을 불러오는데 실패했습니다', { type: 'error' });
          } else {
            setCoupons(data || []);
          }
          setFetchingCoupons(false);
        });
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!selectedCouponId) {
      notify('쿠폰을 선택해주세요', { type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const selectedCoupon = coupons.find(c => c.id === selectedCouponId);

      // 이미 발급된 쿠폰인지 확인
      const { data: existingCoupon } = await supabaseAdmin
        .from('UserCoupon')
        .select('id')
        .eq('userId', userId)
        .eq('couponId', selectedCouponId)
        .single();

      if (existingCoupon) {
        notify(`${userName}님에게 이미 발급된 쿠폰입니다`, { type: 'warning' });
        setLoading(false);
        return;
      }

      // UserCoupon 생성
      const { error } = await supabaseAdmin
        .from('UserCoupon')
        .insert({
          id: crypto.randomUUID(),
          userId: userId,
          couponId: selectedCouponId,
          createdAt: new Date().toISOString(),
        });

      if (error) throw error;

      notify(`${userName}님에게 "${selectedCoupon?.name}" 쿠폰 발급 완료`, { type: 'success' });
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('쿠폰 발급 실패:', error);
      notify(`쿠폰 발급 실패: ${error.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedCouponId('');
    onClose();
  };

  const selectedCoupon = coupons.find(c => c.id === selectedCouponId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CouponIcon color="primary" />
          <span>쿠폰 발급</span>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>{userName}</strong>님에게 쿠폰을 발급합니다.
          </Alert>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>쿠폰 선택</InputLabel>
            <Select
              value={selectedCouponId}
              label="쿠폰 선택"
              onChange={(e) => setSelectedCouponId(e.target.value)}
              disabled={fetchingCoupons}
            >
              {fetchingCoupons ? (
                <MenuItem disabled>불러오는 중...</MenuItem>
              ) : coupons.length === 0 ? (
                <MenuItem disabled>발급 가능한 쿠폰이 없습니다</MenuItem>
              ) : (
                coupons.map((coupon) => (
                  <MenuItem key={coupon.id} value={coupon.id}>
                    {coupon.name} ({coupon.type === 'PERCENTAGE' ? `${coupon.value}%` : `${Number(coupon.value).toLocaleString()}원`} 할인)
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {selectedCoupon && (
            <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
              <Typography variant="subtitle2" gutterBottom>쿠폰 정보</Typography>
              <Box sx={{ display: 'grid', gap: 0.5 }}>
                <Typography variant="body2">
                  <strong>쿠폰명:</strong> {selectedCoupon.name}
                </Typography>
                <Typography variant="body2">
                  <strong>할인:</strong> {selectedCoupon.type === 'PERCENTAGE' ? `${selectedCoupon.value}%` : `${Number(selectedCoupon.value).toLocaleString()}원`}
                </Typography>
                <Typography variant="body2">
                  <strong>최소 주문금액:</strong> {selectedCoupon.minOrderAmount?.toLocaleString() || 0}원
                </Typography>
                {selectedCoupon.maxDiscountAmount && (
                  <Typography variant="body2">
                    <strong>최대 할인:</strong> {selectedCoupon.maxDiscountAmount?.toLocaleString()}원
                  </Typography>
                )}
                {selectedCoupon.validTo && (
                  <Typography variant="body2">
                    <strong>유효기간:</strong> {new Date(selectedCoupon.validTo).toLocaleDateString('ko-KR')}까지
                  </Typography>
                )}
              </Box>
            </Paper>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={handleClose} disabled={loading}>
          취소
        </MuiButton>
        <MuiButton
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedCouponId}
        >
          {loading ? '처리 중...' : '발급하기'}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
};

// 사용자 필터
const UserFilter = (props: any) => (
  <Filter {...props}>
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextInput label="검색" source="q" alwaysOn fullWidth />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <TextInput label="이메일" source="email" fullWidth />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <TextInput label="이름" source="name" fullWidth />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <TextInput label="전화번호" source="phone" fullWidth />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <SelectInput
          source="role"
          label="권한"
          choices={[
            { id: 'USER', name: '일반회원' },
            { id: 'VIP', name: 'VIP' },
            { id: 'ADMIN', name: '관리자' },
            { id: 'SUPER_ADMIN', name: '최고관리자' },
          ]}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <SelectInput
          source="provider"
          label="가입경로"
          choices={[
            { id: 'EMAIL', name: '이메일' },
            { id: 'KAKAO', name: '카카오' },
            { id: 'NAVER', name: '네이버' },
            { id: 'GOOGLE', name: '구글' },
          ]}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <SelectInput
          source="status"
          label="상태"
          choices={[
            { id: 'ACTIVE', name: '정상' },
            { id: 'SUSPENDED', name: '정지' },
            { id: 'WITHDRAWN', name: '탈퇴' },
          ]}
          fullWidth
        />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <BooleanInput source="emailVerified" label="이메일 인증됨" />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <BooleanInput source="isVip" label="VIP만" />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <DateInput source="createdAt_gte" label="가입일 시작" fullWidth />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <DateInput source="createdAt_lte" label="가입일 종료" fullWidth />
      </Grid>
    </Grid>
  </Filter>
);

// 리스트 액션
const ListActions = () => (
  <TopToolbar>
    <CreateButton />
    <ExportButton />
  </TopToolbar>
);

// 일괄 적립금 지급 다이얼로그
interface BulkPointDialogProps {
  open: boolean;
  onClose: () => void;
  selectedIds: string[];
  onSuccess: () => void;
}

const BulkPointGiveDialog = ({ open, onClose, selectedIds, onSuccess }: BulkPointDialogProps) => {
  const [amount, setAmount] = useState<number>(0);
  const [reason, setReason] = useState('');
  const [expiryDate, setExpiryDate] = useState(getDefaultExpiryDate());
  const [loading, setLoading] = useState(false);
  const notify = useNotify();

  const handleSubmit = async () => {
    if (amount === 0) {
      notify('지급/차감할 적립금을 입력해주세요', { type: 'warning' });
      return;
    }
    if (!reason.trim()) {
      notify('지급 사유를 입력해주세요', { type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      // 선택된 사용자들의 현재 적립금 조회
      const { data: users, error: fetchError } = await supabaseAdmin
        .from('User')
        .select('id, name, points')
        .in('id', selectedIds);

      if (fetchError) throw fetchError;

      // 각 사용자에게 적립금 지급
      for (const user of users || []) {
        const newBalance = (user.points || 0) + amount;

        // User.points 업데이트
        await supabaseAdmin
          .from('User')
          .update({ points: newBalance })
          .eq('id', user.id);

        // PointHistory 생성
        await supabaseAdmin
          .from('PointHistory')
          .insert({
            id: crypto.randomUUID(),
            userId: user.id,
            type: amount > 0 ? 'EARNED' : 'USED',
            amount: amount,
            balance: newBalance,
            description: reason,
            expiresAt: expiryDate ? new Date(expiryDate).toISOString() : null,
            createdAt: new Date().toISOString(),
          });
      }

      notify(`${selectedIds.length}명에게 ${amount > 0 ? amount.toLocaleString() + 'P 지급' : Math.abs(amount).toLocaleString() + 'P 차감'} 완료`, { type: 'success' });
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('일괄 적립금 지급 실패:', error);
      notify(`일괄 적립금 처리 실패: ${error.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAmount(0);
    setReason('');
    setExpiryDate(getDefaultExpiryDate());
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <LocalAtmIcon color="primary" />
          <span>일괄 적립금 지급/차감</span>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>{selectedIds.length}명</strong>의 회원에게 적립금을 지급/차감합니다.
          </Alert>

          <MuiTextField
            label="적립금 금액"
            type="number"
            value={amount}
            onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
            fullWidth
            sx={{ mb: 2 }}
            helperText="양수: 지급 / 음수: 차감"
            InputProps={{
              endAdornment: <InputAdornment position="end">P</InputAdornment>,
            }}
          />

          <MuiTextField
            label="지급/차감 사유"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            rows={2}
            sx={{ mb: 2 }}
            placeholder="예: 이벤트 참여 보상, 프로모션 등"
          />

          <MuiTextField
            label="만료일 (선택사항)"
            type="date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            helperText="기본값: 1년 후 (수정 가능)"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={handleClose} disabled={loading}>
          취소
        </MuiButton>
        <MuiButton
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || amount === 0 || !reason.trim()}
        >
          {loading ? '처리 중...' : `${selectedIds.length}명에게 ${amount >= 0 ? '지급' : '차감'}하기`}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
};

// 일괄 쿠폰 발급 다이얼로그
interface BulkCouponDialogProps {
  open: boolean;
  onClose: () => void;
  selectedIds: string[];
  onSuccess: () => void;
}

const BulkCouponGiveDialog = ({ open, onClose, selectedIds, onSuccess }: BulkCouponDialogProps) => {
  const [coupons, setCoupons] = useState<any[]>([]);
  const [selectedCouponId, setSelectedCouponId] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingCoupons, setFetchingCoupons] = useState(false);
  const notify = useNotify();

  // 쿠폰 목록 조회
  React.useEffect(() => {
    if (open) {
      setFetchingCoupons(true);
      supabaseAdmin
        .from('Coupon')
        .select('*')
        .eq('isActive', true)
        .order('createdAt', { ascending: false })
        .then(({ data, error }) => {
          if (error) {
            console.error('쿠폰 목록 조회 실패:', error);
            notify('쿠폰 목록을 불러오는데 실패했습니다', { type: 'error' });
          } else {
            setCoupons(data || []);
          }
          setFetchingCoupons(false);
        });
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!selectedCouponId) {
      notify('쿠폰을 선택해주세요', { type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const selectedCoupon = coupons.find(c => c.id === selectedCouponId);

      // 이미 발급된 유저 확인
      const { data: existingCoupons } = await supabaseAdmin
        .from('UserCoupon')
        .select('userId')
        .eq('couponId', selectedCouponId)
        .in('userId', selectedIds);

      const alreadyIssuedUserIds = new Set((existingCoupons || []).map(c => c.userId));
      const newUserIds = selectedIds.filter(id => !alreadyIssuedUserIds.has(id));

      if (newUserIds.length === 0) {
        notify('선택한 모든 회원에게 이미 발급된 쿠폰입니다', { type: 'warning' });
        setLoading(false);
        return;
      }

      // 중복되지 않은 사용자에게만 쿠폰 발급
      const insertData = newUserIds.map(userId => ({
        id: crypto.randomUUID(),
        userId: userId,
        couponId: selectedCouponId,
        createdAt: new Date().toISOString(),
      }));

      const { error } = await supabaseAdmin
        .from('UserCoupon')
        .insert(insertData);

      if (error) throw error;

      const skippedCount = selectedIds.length - newUserIds.length;
      const message = skippedCount > 0
        ? `${newUserIds.length}명에게 "${selectedCoupon?.name}" 쿠폰 발급 완료 (${skippedCount}명은 이미 발급됨)`
        : `${newUserIds.length}명에게 "${selectedCoupon?.name}" 쿠폰 발급 완료`;
      notify(message, { type: 'success' });
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('일괄 쿠폰 발급 실패:', error);
      notify(`일괄 쿠폰 발급 실패: ${error.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedCouponId('');
    onClose();
  };

  const selectedCoupon = coupons.find(c => c.id === selectedCouponId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CouponIcon color="primary" />
          <span>일괄 쿠폰 발급</span>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>{selectedIds.length}명</strong>의 회원에게 쿠폰을 발급합니다.
          </Alert>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>쿠폰 선택</InputLabel>
            <Select
              value={selectedCouponId}
              label="쿠폰 선택"
              onChange={(e) => setSelectedCouponId(e.target.value)}
              disabled={fetchingCoupons}
            >
              {fetchingCoupons ? (
                <MenuItem disabled>불러오는 중...</MenuItem>
              ) : coupons.length === 0 ? (
                <MenuItem disabled>발급 가능한 쿠폰이 없습니다</MenuItem>
              ) : (
                coupons.map((coupon) => (
                  <MenuItem key={coupon.id} value={coupon.id}>
                    {coupon.name} ({coupon.type === 'PERCENTAGE' ? `${coupon.value}%` : `${Number(coupon.value).toLocaleString()}원`} 할인)
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {selectedCoupon && (
            <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
              <Typography variant="subtitle2" gutterBottom>쿠폰 정보</Typography>
              <Box sx={{ display: 'grid', gap: 0.5 }}>
                <Typography variant="body2">
                  <strong>쿠폰명:</strong> {selectedCoupon.name}
                </Typography>
                <Typography variant="body2">
                  <strong>할인:</strong> {selectedCoupon.type === 'PERCENTAGE' ? `${selectedCoupon.value}%` : `${Number(selectedCoupon.value).toLocaleString()}원`}
                </Typography>
                <Typography variant="body2">
                  <strong>최소 주문금액:</strong> {selectedCoupon.minOrderAmount?.toLocaleString() || 0}원
                </Typography>
                {selectedCoupon.maxDiscountAmount && (
                  <Typography variant="body2">
                    <strong>최대 할인:</strong> {selectedCoupon.maxDiscountAmount?.toLocaleString()}원
                  </Typography>
                )}
              </Box>
            </Paper>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={handleClose} disabled={loading}>
          취소
        </MuiButton>
        <MuiButton
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedCouponId}
        >
          {loading ? '처리 중...' : `${selectedIds.length}명에게 발급하기`}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
};

// 벌크 액션
const BulkActionButtons = () => {
  const [pointDialogOpen, setPointDialogOpen] = useState(false);
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const { selectedIds } = useListContext();
  const unselectAll = useUnselectAll('users');
  const refresh = useRefresh();

  const handleSuccess = () => {
    unselectAll();
    refresh();
  };

  return (
    <>
      <BulkUpdateButton
        label="VIP 설정"
        data={{ isVip: true }}
      />
      <BulkUpdateButton
        label="계정 정지"
        data={{ status: 'SUSPENDED' }}
      />
      <MuiButton
        size="small"
        startIcon={<LocalAtmIcon />}
        onClick={() => setPointDialogOpen(true)}
        sx={{ ml: 1 }}
      >
        적립금 지급
      </MuiButton>
      <MuiButton
        size="small"
        startIcon={<CouponIcon />}
        onClick={() => setCouponDialogOpen(true)}
        sx={{ ml: 1 }}
      >
        쿠폰 발급
      </MuiButton>
      <BulkDeleteButton />

      <BulkPointGiveDialog
        open={pointDialogOpen}
        onClose={() => setPointDialogOpen(false)}
        selectedIds={selectedIds as string[]}
        onSuccess={handleSuccess}
      />
      <BulkCouponGiveDialog
        open={couponDialogOpen}
        onClose={() => setCouponDialogOpen(false)}
        selectedIds={selectedIds as string[]}
        onSuccess={handleSuccess}
      />
    </>
  );
};

// 회원 등급 라벨 및 색상
const MEMBERSHIP_CONFIG: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
  BRONZE: { label: '브론즈', color: 'default' },
  SILVER: { label: '실버', color: 'info' },
  GOLD: { label: '골드', color: 'warning' },
  PLATINUM: { label: '플래티넘', color: 'secondary' },
  VIP: { label: 'VIP', color: 'error' },
};

// 사용자 등급 표시
const UserGradeChip = ({ user }: any) => {
  const getGrade = () => {
    // 관리자 역할 우선 표시
    if (user.role === 'SUPER_ADMIN') return { label: '최고관리자', color: 'error' as const };
    if (user.role === 'ADMIN') return { label: '관리자', color: 'warning' as const };

    // membershipLevel 기반 등급 표시
    const membershipLevel = user.membershipLevel || 'BRONZE';
    const config = MEMBERSHIP_CONFIG[membershipLevel];
    if (config) {
      return config;
    }

    return { label: '브론즈', color: 'default' as const };
  };

  const grade = getGrade();
  return <Chip label={grade.label} color={grade.color} size="small" />;
};

// 사용자 목록
export const UserList = () => {
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1C1C1E' }}>
          회원
        </Typography>
      </Box>
      <List
        title="회원"
        filters={<UserFilter />}
        actions={<ListActions />}
        bulkActionButtons={<BulkActionButtons />}
        perPage={25}
        sort={{ field: 'createdAt', order: 'DESC' }}
      >
        {isSmall ? (
          <SimpleList
            primaryText={(record) => record.name || '이름 없음'}
            secondaryText={(record) => record.email}
            tertiaryText={(record) => {
              const statusConfig: any = {
                ACTIVE: '정상',
                SUSPENDED: '정지',
                WITHDRAWN: '탈퇴',
              };
              return statusConfig[record.status] || '정상';
            }}
            linkType="show"
          />
        ) : (
          <Datagrid rowClick="show" sx={{ '& .RaDatagrid-rowCell': { padding: '12px 8px' } }}>
            {/* 회원 정보 (프로필 + 이름 + 이메일) */}
            <FunctionField
              label="회원 정보"
              render={(record: any) => (
                <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: { xs: "auto", md: 220 } }}>
                  <Avatar src={record.profileImage} sx={{ width: 40, height: 40 }}>
                    {record.name?.[0] || '?'}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                      {record.name || '이름 없음'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1.3 }}>
                      {record.email}
                    </Typography>
                  </Box>
                </Box>
              )}
            />

            {/* 등급/포인트 */}
            <FunctionField
              label="등급/적립금"
              render={(record: any) => (
                <Box sx={{ minWidth: { xs: "auto", md: 100 } }}>
                  <UserGradeChip user={record} />
                  <Typography variant="caption" display="block" color="primary" fontWeight={600} mt={0.5}>
                    {(record.points || 0).toLocaleString()}P
                  </Typography>
                </Box>
              )}
            />

            {/* 주문 실적 */}
            <FunctionField
              label="주문 실적"
              render={(record: any) => (
                <Box minWidth={110}>
                  <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                    {record.orderCount || 0}건
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1.3 }}>
                    ₩{(record.totalPurchaseAmount || 0).toLocaleString()}
                  </Typography>
                </Box>
              )}
            />

            {/* 가입 정보 */}
            <FunctionField
              label="가입 정보"
              render={(record: any) => {
                const providers: any = {
                  EMAIL: '이메일',
                  KAKAO: '카카오',
                  NAVER: '네이버',
                  GOOGLE: '구글',
                };
                return (
                  <Box sx={{ minWidth: { xs: "auto", md: 100 } }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                      {new Date(record.createdAt).toLocaleDateString('ko-KR')}
                    </Typography>
                    <Chip
                      label={providers[record.provider] || record.provider || '이메일'}
                      size="small"
                      sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                );
              }}
            />

            {/* 계정 상태 */}
            <FunctionField
              label="계정 상태"
              render={(record: any) => {
                const statusConfig: any = {
                  ACTIVE: { label: '정상', color: 'success' },
                  SUSPENDED: { label: '정지', color: 'error' },
                  WITHDRAWN: { label: '탈퇴', color: 'default' },
                };
                const config = statusConfig[record.status] || { label: '정상', color: 'success' };
                return (
                  <Box minWidth={70}>
                    <Chip label={config.label} color={config.color} size="small" />
                  </Box>
                );
              }}
            />

            {/* 액션 */}
            <EditButton />
          </Datagrid>
        )}
      </List>
    </>
  );
};

// 사용자 상세보기 내부 컴포넌트
const UserShowContent = () => {
  const record = useRecordContext();
  const [tabValue, setTabValue] = React.useState(0);
  const [pointDialogOpen, setPointDialogOpen] = useState(false);
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const [availablePoints, setAvailablePoints] = useState<number>(0);
  const refresh = useRefresh();

  // 만료 체크된 적립금 계산
  React.useEffect(() => {
    if (record?.id) {
      calculateAvailablePoints(record.id).then(setAvailablePoints);
    }
  }, [record?.id]);

  if (!record) return null;

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        {/* 기본 정보 */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
                <Avatar sx={{ width: 100, height: 100, mb: 2 }}>
                  <PersonIcon sx={{ fontSize: 50 }} />
                </Avatar>
                <Typography variant="h6">
                  {record.name || '이름 없음'}
                </Typography>
                <UserGradeChip user={record} />
              </Box>

              <Divider sx={{ my: 2 }} />

              <MuiList dense>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={record.email}
                    secondary="이메일"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={record.phone || '-'}
                    secondary="전화번호"
                  />
                </ListItem>
              </MuiList>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  가입 정보
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {record.emailVerified && (
                    <Chip
                      icon={<VerifiedIcon />}
                      label="이메일 인증"
                      color="success"
                      size="small"
                    />
                  )}
                  {record.provider && record.provider !== 'EMAIL' && (
                    <Chip label={`${record.provider} 연동`} size="small" />
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 상세 정보 탭 */}
        <Grid item xs={12} md={8}>
          <Card>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                <Tab label="회원 정보" />
                <Tab label="주문 내역" />
                <Tab label="배송지" />
                <Tab label="적립금" />
                <Tab label="쿠폰" />
                <Tab label="리뷰" />
                <Tab label="문의" />
              </Tabs>
            </Box>

            <CardContent>
              {tabValue === 0 && (
                <SimpleShowLayout>
                  <TextField source="id" label="회원 ID" />
                  <BooleanField source="emailVerified" label="이메일 인증" />
                  <DateField source="termsAcceptedAt" label="이용약관 동의일" showTime />
                  <DateField source="privacyAcceptedAt" label="개인정보 동의일" showTime />
                  <DateField source="marketingAcceptedAt" label="마케팅 동의일" showTime />
                  <DateField source="lastLoginAt" label="마지막 로그인" showTime />
                  <DateField source="createdAt" label="가입일" showTime />
                  <DateField source="updatedAt" label="수정일" showTime />
                </SimpleShowLayout>
              )}

              {tabValue === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    주문 내역
                  </Typography>
                  <ReferenceManyField reference="orders" target="user_id" label="">
                    <Datagrid>
                      <TextField source="order_number" label="주문번호" />
                      <DateField source="created_at" label="주문일" />
                      <NumberField
                        source="total_amount"
                        label="금액"
                        options={{
                          style: 'currency',
                          currency: 'KRW',
                        }}
                      />
                      <FunctionField
                        label="상태"
                        render={(record: any) => {
                          const statusMap: Record<string, string> = {
                            PENDING: '주문접수',
                            PAID: '결제완료',
                            PREPARING: '상품준비중',
                            SHIPPED: '배송중',
                            DELIVERED: '배송완료',
                            CANCELLED: '취소됨',
                            REFUNDED: '환불완료',
                          };
                          return statusMap[record.status] || record.status;
                        }}
                      />
                    </Datagrid>
                  </ReferenceManyField>
                </Box>
              )}

              {tabValue === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    배송지 목록
                  </Typography>
                  <ReferenceManyField reference="addresses" target="userId" label="">
                    <Datagrid bulkActionButtons={false}>
                      <TextField source="name" label="수취인명" />
                      <TextField source="phone" label="연락처" />
                      <FunctionField
                        label="주소"
                        render={(r: any) =>
                          `[${r.zipCode || ''}] ${r.address || ''} ${r.addressDetail || ''}`
                        }
                      />
                      <BooleanField source="isDefault" label="기본배송지" />
                      <DateField source="createdAt" label="등록일" />
                    </Datagrid>
                  </ReferenceManyField>
                </Box>
              )}

              {tabValue === 3 && (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Alert severity="info" sx={{ flex: 1, mr: 2 }}>
                      현재 적립금: <strong>{availablePoints.toLocaleString()}P</strong>
                    </Alert>
                    <MuiButton
                      variant="contained"
                      startIcon={<LocalAtmIcon />}
                      onClick={() => setPointDialogOpen(true)}
                    >
                      적립금 지급
                    </MuiButton>
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    적립금 내역
                  </Typography>
                  <ReferenceManyField reference="point-history" target="userId" label="" sort={{ field: 'createdAt', order: 'DESC' }}>
                    <Datagrid bulkActionButtons={false}>
                      <FunctionField
                        source="createdAt"
                        label="일시"
                        render={(record: any) => formatKSTDateTime(record?.createdAt)}
                      />
                      <FunctionField
                        label="구분"
                        render={(r: any) => (
                          <Chip
                            label={r.type === 'EARNED' ? '적립' : r.type === 'USED' ? '사용' : r.type}
                            color={r.type === 'EARNED' ? 'success' : 'default'}
                            size="small"
                          />
                        )}
                      />
                      <TextField source="description" label="내용" />
                      <FunctionField
                        label="적립금"
                        render={(r: any) => {
                          const today = new Date().toLocaleString('en-CA', { timeZone: 'Asia/Seoul' }).split(',')[0];
                          const expiresDate = r.expiresAt ? r.expiresAt.split('T')[0] : null;
                          const isExpired = r.type === 'EARNED' && expiresDate && expiresDate <= today;
                          return (
                            <Typography
                              color={isExpired ? 'text.disabled' : r.amount > 0 ? 'success.main' : 'error.main'}
                              fontWeight={600}
                              sx={isExpired ? { textDecoration: 'line-through' } : {}}
                            >
                              {r.amount > 0 ? '+' : ''}{r.amount?.toLocaleString()}P
                            </Typography>
                          );
                        }}
                      />
                      <FunctionField
                        label="만료일"
                        render={(r: any) => {
                          if (!r.expiresAt) return '-';
                          const today = new Date().toLocaleString('en-CA', { timeZone: 'Asia/Seoul' }).split(',')[0];
                          const expiresDate = r.expiresAt.split('T')[0];
                          const isExpired = expiresDate <= today;
                          return (
                            <Box>
                              <Typography variant="body2" color={isExpired ? 'error.main' : 'text.primary'}>
                                {new Date(r.expiresAt).toLocaleDateString('ko-KR')}
                              </Typography>
                              {isExpired && r.type === 'EARNED' && (
                                <Chip label="만료" color="error" size="small" sx={{ mt: 0.5 }} />
                              )}
                            </Box>
                          );
                        }}
                      />
                    </Datagrid>
                  </ReferenceManyField>
                </Box>
              )}

              {tabValue === 4 && (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      보유 쿠폰
                    </Typography>
                    <MuiButton
                      variant="contained"
                      startIcon={<CouponIcon />}
                      onClick={() => setCouponDialogOpen(true)}
                    >
                      쿠폰 발급
                    </MuiButton>
                  </Box>
                  <ReferenceManyField reference="user-coupons" target="userId" label="">
                    <Datagrid bulkActionButtons={false}>
                      <ReferenceField source="couponId" reference="coupons" label="쿠폰명">
                        <TextField source="name" />
                      </ReferenceField>
                      <ReferenceField source="couponId" reference="coupons" label="할인">
                        <FunctionField
                          render={(coupon: any) =>
                            coupon.type === 'PERCENTAGE'
                              ? `${coupon.value}%`
                              : `${Number(coupon.value).toLocaleString()}원`
                          }
                        />
                      </ReferenceField>
                      <DateField source="createdAt" label="발급일" />
                      <FunctionField
                        label="상태"
                        render={(r: any) => (
                          <Chip
                            label={r.usedAt ? '사용완료' : '미사용'}
                            color={r.usedAt ? 'default' : 'success'}
                            size="small"
                          />
                        )}
                      />
                      <DateField source="usedAt" label="사용일" emptyText="-" />
                    </Datagrid>
                  </ReferenceManyField>
                </Box>
              )}

              {tabValue === 5 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    작성 리뷰
                  </Typography>
                  <ReferenceManyField reference="reviews" target="userId" label="">
                    <Datagrid>
                      <DateField source="createdAt" label="작성일" />
                      <TextField source="productName" label="상품명" />
                      <NumberField source="rating" label="평점" />
                      <TextField source="comment" label="내용" />
                      <BooleanField source="isVerified" label="구매 인증" />
                    </Datagrid>
                  </ReferenceManyField>
                </Box>
              )}

              {tabValue === 6 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    문의 내역
                  </Typography>
                  <ReferenceManyField reference="inquiries" target="userId" label="">
                    <Datagrid>
                      <DateField source="createdAt" label="문의일" />
                      <TextField source="category" label="카테고리" />
                      <TextField source="title" label="제목" />
                      <TextField source="status" label="상태" />
                      <DateField source="answeredAt" label="답변일" />
                    </Datagrid>
                  </ReferenceManyField>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 통계 정보 */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', minHeight: 100, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  총 주문 수
                </Typography>
                <Typography variant="h4" color="primary" fontWeight={700}>
                  {record.orderCount || 0}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', minHeight: 100, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  총 구매액
                </Typography>
                <Typography variant="h4" color="primary" fontWeight={700}>
                  ₩{(record.totalPurchaseAmount || 0).toLocaleString()}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', minHeight: 100, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  리뷰 수
                </Typography>
                <Typography variant="h4" color="primary" fontWeight={700}>
                  {record.reviewCount || 0}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', minHeight: 100, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  보유 적립금
                </Typography>
                <Typography variant="h4" color="primary" fontWeight={700}>
                  {availablePoints.toLocaleString()}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* 적립금 지급 다이얼로그 */}
      <PointGiveDialog
        open={pointDialogOpen}
        onClose={() => setPointDialogOpen(false)}
        userId={record.id}
        userName={record.name || '회원'}
        currentPoints={availablePoints}
        onSuccess={() => {
          refresh();
          calculateAvailablePoints(record.id).then(setAvailablePoints);
        }}
      />

      {/* 쿠폰 발급 다이얼로그 */}
      <CouponGiveDialog
        open={couponDialogOpen}
        onClose={() => setCouponDialogOpen(false)}
        userId={record.id}
        userName={record.name || '회원'}
        onSuccess={refresh}
      />
    </Box>
  );
};

// 사용자 상세보기
export const UserShow = () => {
  const UserTitle = () => {
    const record = useRecordContext();
    return <span>회원 상세: {record?.name}</span>;
  };

  return (
    <Show title={<UserTitle />}>
      <UserShowContent />
    </Show>
  );
};

// 사용자 수정
export const UserEdit = () => {
  const notify = useNotify();
  const refresh = useRefresh();

  const UserEditTitle = () => {
    const record = useRecordContext();
    return <span>회원 수정: {record?.name}</span>;
  };

  return (
    <Edit title={<UserEditTitle />}>
      <SimpleForm>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Alert severity="warning">
              회원 정보 수정 시 주의가 필요합니다. 권한 변경은 신중하게 진행하세요.
            </Alert>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              기본 정보
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextInput source="name" label="이름" validate={required()} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput source="email" label="이메일" validate={[required(), email()]} fullWidth disabled />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput source="phone" label="전화번호" fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <DateInput source="birthDate" label="생년월일" fullWidth />
          </Grid>
          <Grid item xs={12}>
            <TextInput source="address" label="주소" fullWidth />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              권한 및 상태
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <SelectInput
              source="role"
              label="권한"
              choices={[
                { id: 'USER', name: '일반회원' },
                { id: 'VIP', name: 'VIP' },
                { id: 'ADMIN', name: '관리자' },
                { id: 'SUPER_ADMIN', name: '최고관리자' },
              ]}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectInput
              source="status"
              label="계정 상태"
              choices={[
                { id: 'ACTIVE', name: '정상' },
                { id: 'SUSPENDED', name: '정지' },
                { id: 'WITHDRAWN', name: '탈퇴' },
              ]}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <BooleanInput source="emailVerified" label="이메일 인증" />
          </Grid>
          <Grid item xs={12} md={4}>
            <BooleanInput source="isVip" label="VIP 회원" />
          </Grid>
          <Grid item xs={12} md={4}>
            <BooleanInput source="agreeMarketing" label="마케팅 수신 동의" />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              적립금 관리
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <NumberInput source="points" label="보유 적립금" fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <NumberInput source="totalEarnedPoints" label="누적 적립금" fullWidth disabled />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              관리자 메모
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextInput source="adminNote" label="관리자 메모" multiline rows={3} fullWidth />
          </Grid>

          <Grid item xs={12}>
            <Alert severity="info">
              비밀번호 재설정은 회원에게 이메일로 재설정 링크를 발송합니다.
            </Alert>
            <Box mt={2}>
              <Button
                label="비밀번호 재설정 이메일 발송"
                variant="outlined"
                onClick={() => {
                  notify('비밀번호 재설정 이메일을 발송했습니다', { type: 'success' });
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </SimpleForm>
    </Edit>
  );
};

// 사용자 생성
export const UserCreate = () => (
  <Create>
    <SimpleForm>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            회원 등록
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextInput source="name" label="이름" validate={required()} fullWidth />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput source="email" label="이메일" validate={[required(), email()]} fullWidth />
        </Grid>
        <Grid item xs={12} md={6}>
          <PasswordInput source="password" label="비밀번호" validate={[required(), minLength(8)]} fullWidth />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput source="phone" label="전화번호" fullWidth />
        </Grid>
        <Grid item xs={12} md={6}>
          <DateInput source="birthDate" label="생년월일" fullWidth />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectInput
            source="role"
            label="권한"
            choices={[
              { id: 'USER', name: '일반회원' },
              { id: 'VIP', name: 'VIP' },
              { id: 'ADMIN', name: '관리자' },
              { id: 'SUPER_ADMIN', name: '최고관리자' },
            ]}
            defaultValue="USER"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextInput source="address" label="주소" fullWidth />
        </Grid>

        <Grid item xs={12} md={4}>
          <BooleanInput source="emailVerified" label="이메일 인증 완료" defaultValue={false} />
        </Grid>
        <Grid item xs={12} md={4}>
          <BooleanInput source="isVip" label="VIP 회원" defaultValue={false} />
        </Grid>
        <Grid item xs={12} md={4}>
          <BooleanInput source="agreeMarketing" label="마케팅 수신 동의" defaultValue={false} />
        </Grid>
      </Grid>
    </SimpleForm>
  </Create>
);